import { SettingsPermissions } from "@redotech/account-common/permissions";
import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import DownArrowIcon from "@redotech/redo-icons/arrow-down.svg";
import UpArrowIcon from "@redotech/redo-icons/arrow-up.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import {
  ChatChoiceState,
  ChatMultipleChoiceState,
} from "@redotech/redo-return-ui/flows/chat-flow/chat-multiple-choice";
import { containsImg } from "@redotech/redo-return-ui/flows/chat-flow/step-rich-text-util";
import { RequiredMessage } from "@redotech/redo-return-ui/flows/required-message";
import * as multipleChoiceCss from "@redotech/redo-return-ui/flows/return-flow/multiple-choice.module.css";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { InputTheme, TextInput } from "@redotech/redo-web/text-input";
import { produce } from "immer";
import Quill from "quill";
import { memo, useRef } from "react";
import { StepRichTextEditor } from "./step-rich-text";

export const ChatMultipleChoiceDetails = memo(
  function ChatMultipleChoiceDetails({
    stepSelect,
    state,
    setState,
  }: StepTypeDetailsProps<ChatMultipleChoiceState>) {
    const { t } = useTranslation();
    // TODO: Not sure what the right permission mix is
    const canEditSupportSettings = useHasPermission(
      SettingsPermissions.SET_SUPPORT_SETTINGS,
    );
    const canEditReturnsSettings = useHasPermission(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    );
    const canEditSettings = canEditSupportSettings || canEditReturnsSettings;

    const messageQuillRef = useRef<Quill | undefined>(undefined);

    const handleAdd = useHandler(() =>
      setState((state) => ({
        ...state,
        choices: [...state.choices, { name: "", next: null, nextName: null }],
      })),
    );

    const handleMessageChange = useHandler(() => {
      if (!messageQuillRef.current) {
        return;
      }
      let message = messageQuillRef.current.getText();
      let messageHtml: string | undefined =
        messageQuillRef.current.getSemanticHTML();
      if (message === "\n") {
        message = "";
      }
      if (messageHtml === "<p></p>") {
        messageHtml = undefined;
      }

      if (messageHtml !== state.messageHtml || message !== state.message) {
        setState((state) => ({ ...state, message, messageHtml }));
      }
    });

    const handleFocus = useHandler((focused: boolean) => {
      if (!focused) {
        setState((state) => ({ ...state, attempted: true }));
      }
    });

    return (
      <>
        <LabeledInput
          errors={
            containsImg(state.messageHtml) ? [t("Cannot contain images")] : []
          }
          label={t("Question")}
        >
          <StepRichTextEditor
            htmlValue={state.messageHtml}
            onTextChange={handleMessageChange}
            quillRef={messageQuillRef}
            readOnly={!canEditSettings}
            textValue={state.message}
          />
        </LabeledInput>
        <IterableMap items={state.choices} keyFn={(_, index) => index}>
          {(choice, index) => (
            <StateChoiceComponent
              canEditSettings={canEditSettings}
              choice={choice}
              handleFocus={handleFocus}
              index={index}
              setState={setState}
              state={state}
              stepSelect={stepSelect}
            />
          )}
        </IterableMap>
        <Divider />
        <div>
          <RedoButton
            disabled={!canEditSettings}
            hierarchy="secondary"
            onClick={handleAdd}
            size="sm"
            text={t("+ Add option", {
              context: "button label; keep the leading plus sign",
            })}
            theme="normal"
          />
        </div>
        {!state.choices.length && (
          <RequiredMessage>
            {t("Must have at least one option")}
          </RequiredMessage>
        )}
      </>
    );
  },
);

const StateChoiceComponent = memo(function StateChoiceComponent({
  state,
  setState,
  stepSelect,
  handleFocus,
  canEditSettings,
  choice,
  index,
}: {
  state: ChatMultipleChoiceState;
  setState: StepTypeDetailsProps<ChatMultipleChoiceState>["setState"];
  stepSelect: StepTypeDetailsProps<ChatMultipleChoiceState>["stepSelect"];
  handleFocus(focused: boolean): void;
  canEditSettings: boolean;
  choice: ChatChoiceState;
  index: number;
}) {
  const { t } = useTranslation();
  const setName = useHandler((name: string) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].name = name;
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].nextName = null;
      }),
    ),
  );
  const setStep = useHandler((next: StepId | undefined | "base") => {
    if (next === "base") {
      setState((state) =>
        produce(state, (state) => {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.choices[index].next = null;
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.choices[index].nextName = "base";
        }),
      );
    } else {
      setState((state) =>
        produce(state, (state) => {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.choices[index].next = next || null;
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.choices[index].nextName = null;
        }),
      );
    }
  });
  const handleUp = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index - 1, 0, ...choices);
      }),
    );
  });
  const handleDown = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index + 1, 0, ...choices);
      }),
    );
  });
  const handleDelete = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        state.choices.splice(index, 1);
      }),
    );
  });
  return (
    <>
      <Divider />
      <LabeledInput
        label={
          <div className={multipleChoiceCss.label}>
            <div>{t("Option #{{number}}", { number: index + 1 })}</div>
            {canEditSettings && (
              <>
                <RedoButton
                  aria-label={t("Move up")}
                  disabled={!index}
                  hierarchy="tertiary"
                  IconLeading={UpArrowIcon}
                  onClick={(event) => {
                    handleUp();
                    event.stopPropagation();
                  }}
                  size="sm"
                  theme="normal"
                />
                <RedoButton
                  aria-label={t("Move down")}
                  disabled={index === state.choices.length - 1}
                  hierarchy="tertiary"
                  IconLeading={DownArrowIcon}
                  onClick={(event) => {
                    handleDown();
                    event.stopPropagation();
                  }}
                  size="sm"
                  theme="normal"
                />
                <RedoButton
                  aria-label={t("Delete")}
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={(event) => {
                    handleDelete();
                    event.stopPropagation();
                  }}
                  size="sm"
                  theme="normal"
                />
              </>
            )}
          </div>
        }
      >
        <TextInput
          error={state.attempted && !choice.name}
          onChange={setName}
          onFocus={(focused: boolean) => {
            if (!choice.name) {
              handleFocus(focused);
            }
          }}
          readonly={!canEditSettings}
          theme={InputTheme.FORM}
          value={choice.name}
        />
      </LabeledInput>
      <LabeledInput
        errors={
          state.attempted && (!choice.next || !choice.nextName)
            ? [t("Required")]
            : []
        }
        label={t("Next step #{{number}}", { number: index + 1 })}
      >
        {stepSelect({
          value: choice.nextName ? choice.nextName : choice.next,
          valueChange: setStep,
          type: ModelStep.CHAT_MULTIPLE_CHOICE,
        })}
      </LabeledInput>
    </>
  );
});
